import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { matchFilterArrayIncludes, matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import * as publicationProductActions from '@redux/modules/publicationProduct'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['publicationProducts']

type LoadPublicationProductIdsParams = {
  // @ts-expect-error TS2304
  dispatch: AppDispatch,
  publicationProductIds: number[],
  requestOptions?: EntityOptions,
}

const loadPublicationProductIds = (params: LoadPublicationProductIdsParams) => {
  const { dispatch, publicationProductIds, requestOptions } = params
  const { loadPublicationProductIds: loadFn } = publicationProductActions

  return dispatch(loadFn(publicationProductIds, requestOptions))
}

function usePublicationProducts(options = {}) {
  // @ts-expect-error TS2339
  const { entityKey, filters } = options
  const {
    publication_id: filterPublicationId,
    publication_ids: filterPublicationIds,
  } = filters || {}

  const dispatch = useDispatch()

  const {
    updatedEntities: { publicationProducts: publicationProductsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  // @ts-expect-error TS18046
  const entities = useSelector(reduxState => reduxState.entities)
  const { publicationProducts } = entities
  const publicationProductsArray = Object.values(publicationProducts)

  const filteredPublicationProducts = useMemo(() => {
    const filtered = Object.values(publicationProducts).filter((publicationProduct) => {
      // @ts-expect-error TS2339
      const { publication_id } = publicationProduct

      const matchPublicationId = matchFilterNumber(publication_id, filterPublicationId)
      const matchPubliactionIds = filterPublicationIds ? matchFilterArrayIncludes(filterPublicationIds, publication_id) : true

      return matchPublicationId && matchPubliactionIds
    })

    // @ts-expect-error TS2345
    return sortArrayBy(filtered, 'asc', 'title')
  }, [publicationProductsUpdatedAt, JSON.stringify(filters)])

  const filteredPublicationProductsCount = filteredPublicationProducts.length
  const hasFilteredPublicationProducts = !!filteredPublicationProductsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPublicationProductsCount,
    // @ts-expect-error TS2339
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const reduxActionPayload = useReduxAction(
    'publicationProducts',
    'loadPublicationProducts',
    {
      ...filtersWithOffset,
      entityKey,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: { loading: boolean }) => performHttpRequests && !loading,
    },
  )

  const { loading: loadingPublicationProducts, reduxActionPerformed } = reduxActionPayload

  return {
    callbacks: {
      loadMore,
      loadPublicationProductIds: (params: { publicationProductIds: number[], entityOptions?: EntityOptions }) => {
        const { publicationProductIds, entityOptions } = params
        return loadPublicationProductIds({ dispatch, publicationProductIds, requestOptions: entityOptions })
      },
    },
    canLoadMore,
    filteredPublicationProducts,
    filteredPublicationProductsCount,
    hasFilteredPublicationProducts,
    loading: loadingPublicationProducts,
    publicationProducts,
    publicationProductsArray,
    reduxActionPerformed,
  }
}

export default usePublicationProducts
